/**
 * Utility functions for handling API errors, especially 422 validation errors
 *
 * Usage in form catch blocks:
 *
 * try {
 *   await someApi.create(data);
 * } catch (error) {
 *   handleFormSubmitError(form, error, 'خطا در ایجاد');
 * }
 */

import { message } from 'antd';
import {
  ValidationError,
  ForbiddenError,
  parse422Error,
  show422Error as show422ErrorFromApi,
  isForbiddenError,
  extractApiUserMessage,
  showForbiddenAccessMessage,
} from './api';
import {
  applyValidationErrorsToForm,
  getValidationDetails,
  isValidationError,
  scrollToFirstError,
} from './formHelpers';

export {
  show422ErrorFromApi as show422Error,
  ValidationError,
  parse422Error,
  isValidationError,
  getValidationDetails,
  applyValidationErrorsToForm,
};

/**
 * Handles form submit errors: client-side validation scroll + API 422 field errors.
 * @returns {boolean} true if the error was handled (validation)
 */
export const handleFormSubmitError = (
  form,
  error,
  defaultMessage = 'خطا در انجام عملیات',
  options = {},
) => {
  if (error?.errorFields?.length) {
    scrollToFirstError(form);
    return true;
  }

  if (applyValidationErrorsToForm(form, error, options)) {
    message.warning({
      content: 'لطفاً خطاهای فرم را برطرف کنید',
      duration: 4,
    });
    return true;
  }

  if (isForbiddenError(error)) {
    showForbiddenAccessMessage(
      error?.message || error?.details?.message || error?.details?.error?.message,
    );
    return true;
  }

  message.error(extractApiUserMessage(error, defaultMessage));
  return false;
};

/**
 * Handles API errors and displays appropriate messages
 * @param {Error|Object} error - The error object from API
 * @param {string} defaultMessage - Default error message if not 422
 * @param {import('antd/es/form').FormInstance} [form] - Optional form for field-level 422 errors
 */
export const handleApiError = (error, defaultMessage = 'خطا در انجام عملیات', form = null) => {
  console.error('API Error:', error);

  if (isValidationError(error)) {
    show422ErrorFromApi(error, form);
    return;
  }

  if (isForbiddenError(error)) {
    showForbiddenAccessMessage(
      error?.message || error?.details?.message || error?.details?.error?.message,
    );
    return;
  }

  message.error(extractApiUserMessage(error, defaultMessage));
};

/** نمایش خطای API در هر catch — 403 با پیام فارسی، بدون آبجکت خام */
export const showApiErrorMessage = (error, defaultMessage = 'خطا در انجام عملیات') => {
  if (isForbiddenError(error)) {
    showForbiddenAccessMessage(
      error?.message || error?.details?.message || error?.details?.error?.message,
    );
    return;
  }
  message.error(extractApiUserMessage(error, defaultMessage));
};
